import { useCallback, useEffect, useRef, useState } from 'react';
import { getMediaRecorderOptions } from '@/config/audio.config';
import {
  pauseRecordingClock,
  recordedMs,
  resumeRecordingClock,
  startRecordingClock,
  type RecordingClock,
} from '@/lib/recording-clock';
import type { AudioAnswer } from '../types';

export type RecorderError = 'microphone' | null;

/**
 * A single microphone recording: start, pause, resume, stop, elapsed time and
 * the resulting blob with a playable URL. The URL is revoked when replaced or on
 * unmount. The duration counts recorded time only: a paused stretch is not part
 * of the audio file, so it is not part of the duration either. `start(onRecorded)`
 * hands the finished recording to the caller that started it, so it can be stored
 * against the question it answers.
 */
export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [answer, setAnswer] = useState<AudioAnswer | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<RecorderError>(null);
  // The live stream, for LiveWaveform while recording; null otherwise.
  const [stream, setStream] = useState<MediaStream | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const clockRef = useRef<RecordingClock>(startRecordingClock(0));
  const mountedRef = useRef(true);

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (mountedRef.current) setStream(null);
  };

  // The effect below revokes the previous URL whenever it changes.
  const replaceAnswer = useCallback((next: AudioAnswer | null) => {
    setAnswer(next);
    setUrl(next ? URL.createObjectURL(next.blob) : null);
  }, []);

  useEffect(() => {
    if (!isRecording || isPaused) return undefined;
    const timer = window.setInterval(
      () => setElapsedMs(recordedMs(clockRef.current, Date.now())),
      250,
    );
    return () => window.clearInterval(timer);
  }, [isRecording, isPaused]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      releaseStream();
    };
  }, []);

  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);

  const start = useCallback(async (onRecorded?: (answer: AudioAnswer) => void) => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') return;
    setError(null);
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = media;
      setStream(media);
      const recorder = new MediaRecorder(media, getMediaRecorderOptions());
      recorderRef.current = recorder;
      chunksRef.current = [];
      clockRef.current = startRecordingClock(Date.now());
      setElapsedMs(0);
      setIsPaused(false);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        const durationMs = recordedMs(clockRef.current, Date.now());
        const recorded: AudioAnswer = { blob, durationMs };
        chunksRef.current = [];
        recorderRef.current = null;
        releaseStream();
        onRecorded?.(recorded);
        // Stopped by the unmount cleanup: an object URL created now would never be revoked.
        if (!mountedRef.current) return;
        replaceAnswer(recorded);
        setElapsedMs(durationMs);
        setIsRecording(false);
        setIsPaused(false);
      };

      recorder.start();
      setIsRecording(true);
    } catch {
      releaseStream();
      setIsRecording(false);
      setError('microphone');
    }
  }, [replaceAnswer]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    // A paused recorder must close its open segment before it stops.
    clockRef.current = pauseRecordingClock(clockRef.current, Date.now());
    recorder.stop();
  }, []);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder?.state !== 'recording') return;
    clockRef.current = pauseRecordingClock(clockRef.current, Date.now());
    setElapsedMs(recordedMs(clockRef.current, Date.now()));
    recorder.pause();
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder?.state !== 'paused') return;
    clockRef.current = resumeRecordingClock(clockRef.current, Date.now());
    recorder.resume();
    setIsPaused(false);
  }, []);

  const reset = useCallback(() => {
    replaceAnswer(null);
    setElapsedMs(0);
    setIsPaused(false);
  }, [replaceAnswer]);

  return {
    isRecording,
    isPaused,
    answer,
    url,
    elapsedMs,
    error,
    stream,
    start,
    stop,
    pause,
    resume,
    reset,
    load: replaceAnswer,
  };
}
